import { Box, Button, Grid, Typography } from "@mui/material";
import type { Meta, StoryObj } from "@storybook/react";
import { useEffect, useRef, useState } from "react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-monokai";
import "ace-builds/src-noconflict/ext-language_tools";
import {
  AjvInstance,
  EnumFormGenComponentType,
  type IFormGenSchema,
  type TJSONSchema,
} from "@artifact-management/json-dynamic-form";
import { type Ace, config as aceConfig } from "ace-builds";
import jsonSourceMap from "json-source-map";
import { FormGen } from "./index";

aceConfig.set("basePath", "/node_modules/ace-builds/src-noconflict/");

const jsonSchema = {
  type: "object",
  properties: {
    firstName: {
      type: "string",
      minLength: 1,
      errorMessage: {
        minLength: "Le prénom est requis",
      },
    },
    genre: {
      type: "string",
      enum: ["Homme", "Femme", "Autre"],
      errorMessage: {
        enum: "Le genre est invalide",
      },
    },
    autocomplete: {
      type: "object",
      properties: {
        value: {
          type: "string",
        },
        label: {
          type: "string",
        },
      },
      additionalProperties: false,
      required: ["value", "label"],
      enum: [
        { value: "option1", label: "Option 1" },
        { value: "option2", label: "Option 2" },
      ],
    },
    autocompleteMultiple: {
      type: "array",
      items: {
        type: "object",
        properties: {
          value: {
            type: "string",
          },
          label: {
            type: "string",
          },
        },
        additionalProperties: false,
        required: ["value", "label"],
        enum: [
          { value: "option1", label: "Option 1" },
          { value: "option2", label: "Option 2" },
        ],
      },
    },
    cgu: {
      type: "boolean",
    },
  },
  additionalProperties: false,
  required: ["firstName", "genre"],
  $defs: {
    hideIfJohn: {
      properties: {
        firstName: {
          const: "John",
        },
      },
    },
  },
} as const satisfies TJSONSchema;

export const formSchema: IFormGenSchema<typeof jsonSchema> = {
  version: "1.0",
  jsonSchema,
  uiSchema: [
    {
      type: EnumFormGenComponentType.INPUT,
      label: "Prénom *",
      scope: "firstName",
      columns: {
        xs: 12,
        sm: 6,
      },
    },
    {
      type: EnumFormGenComponentType.INPUT,
      label: "Genre *",
      scope: "genre",
      columns: {
        xs: 12,
        sm: 6,
      },
    },
    {
      type: EnumFormGenComponentType.INPUT,
      label: "Autocomplete",
      scope: "autocomplete",
      columns: {
        xs: 12,
        sm: 6,
      },
      rules: [
        {
          rule: "hideIfJohn",
          override: {
            hidden: true,
          },
        },
      ],
    },
    {
      type: EnumFormGenComponentType.INPUT,
      label: "Autocomplete Multiple",
      scope: "autocompleteMultiple",
      columns: {
        xs: 12,
        sm: 6,
      },
      rules: [
        {
          rule: "hideIfJohn",
          override: {
            columns: { xs: 12 },
          },
        },
      ],
    },
    {
      type: EnumFormGenComponentType.INPUT,
      label: "Genre *",
      scope: "genre",
      columns: {
        xs: 12,
        sm: 6,
      },
    },
    {
      type: EnumFormGenComponentType.INPUT,
      label: "CGU *",
      scope: "cgu",
      columns: {
        xs: 12,
        sm: 6,
      },
    },
  ],
};

const JsonSchemaEditor = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (newValue: string) => void;
}) => {
  const jsonSchemaEditorRef = useRef<Ace.Editor | null>(null);
  const internalValidationRef = useRef<boolean>(false);

  const validateJsonSchema = (annotations: Ace.Annotation[]) => {
    if (internalValidationRef.current) {
      internalValidationRef.current = false;
      return;
    }
    internalValidationRef.current = true;
    const session = jsonSchemaEditorRef.current?.getSession();
    if (!session) {
      return;
    }
    try {
      const parsed = JSON.parse(value);
      const valid = AjvInstance.validateSchema(parsed);
      if (!valid && AjvInstance.errors) {
        const sourceMap = jsonSourceMap.parse(value);
        for (const error of AjvInstance.errors) {
          const errorPointer = sourceMap.pointers[error.instancePath];
          annotations.push({
            row: errorPointer.key ? errorPointer.key.line : 0,
            column: errorPointer.key ? errorPointer.key.column : 0,
            text: `${error.instancePath} ${error.message}`,
            type: "error",
          });
        }
      }
    } catch (error) {}
    session.setAnnotations(annotations);
  };

  return (
    <AceEditor
      mode="json"
      theme="monokai"
      value={value}
      onChange={onChange}
      onValidate={validateJsonSchema}
      editorProps={{ $blockScrolling: true }}
      width="100%"
      setOptions={{
        useWorker: true,
      }}
      onLoad={(editor) => {
        jsonSchemaEditorRef.current = editor;
      }}
    />
  );
};

const UiSchemaEditor = ({
  jsonSchema,
  value,
  onChange,
}: {
  jsonSchema: string;
  value: string;
  onChange: (newValue: string) => void;
}) => {
  const parsedJsonSchemaRef = useRef<TJSONSchema>(formSchema.jsonSchema);

  useEffect(() => {
    try {
      parsedJsonSchemaRef.current = JSON.parse(jsonSchema);
    } catch (error) {}
  }, [jsonSchema]);

  const aceCompleter: Ace.Completer = {
    getCompletions: (editor, session, pos, prefix, callback) => {
      const line = session.getLine(pos.row);
      const scopePropertyMatch = /^\s*"scope"\s*:\s*".*"\s*,?$/.test(line);
      if (scopePropertyMatch) {
        return callback(
          null,
          Object.keys(parsedJsonSchemaRef.current.properties ?? {}).map((key) => ({
            name: key,
            value: key,
            meta: "scope value",
            score: 1000,
          }))
        );
      }
      const rulePropertyMatch = /^\s*"rule"\s*:\s*".*"\s*,?$/.test(line);
      if (rulePropertyMatch) {
        return callback(
          null,
          Object.keys(parsedJsonSchemaRef.current.$defs ?? {}).map((key) => ({
            name: key,
            value: key,
            meta: "rule value",
            score: 1000,
          }))
        );
      }
    },
  };

  return (
    <AceEditor
      mode="json"
      theme="monokai"
      value={value}
      onChange={onChange}
      editorProps={{ $blockScrolling: true }}
      width="100%"
      setOptions={{
        enableBasicAutocompletion: true,
        enableLiveAutocompletion: true,
        useWorker: true,
      }}
      onLoad={(editor) => {
        editor.completers = [aceCompleter];
      }}
    />
  );
};

const FormGenSandbox = () => {
  const [jsonSchema, setJsonSchema] = useState(JSON.stringify(formSchema.jsonSchema, null, 2));
  const [uiSchema, setUiSchema] = useState(JSON.stringify(formSchema.uiSchema, null, 2));
  const [schema, setSchema] = useState(formSchema);
  const [toggle, setToggle] = useState(false);

  const handleUpdateSchema = () => {
    try {
      const parsedJsonSchema = JSON.parse(jsonSchema);
      const parsedUiSchema = JSON.parse(uiSchema);
      setSchema({
        version: "1.0",
        jsonSchema: parsedJsonSchema,
        uiSchema: parsedUiSchema,
      });
      setToggle((prev) => !prev);
    } catch (error) {
      alert("Invalid JSON");
    }
  };

  return (
    <Box sx={{ margin: 2 }}>
      <Grid container spacing={2}>
        <Grid size={6}>
          <Typography variant="h6">JSON Schema</Typography>
          <JsonSchemaEditor value={jsonSchema} onChange={setJsonSchema} />
        </Grid>
        <Grid size={6}>
          <Typography variant="h6">UI Schema</Typography>
          <UiSchemaEditor jsonSchema={jsonSchema} value={uiSchema} onChange={setUiSchema} />
        </Grid>
        <Grid size={12} sx={{ display: "flex", justifyContent: "center" }}>
          <Button onClick={handleUpdateSchema}>Update Schema</Button>
        </Grid>
        <Grid size={12}>
          <FormGen key={toggle.toString()} schema={schema} onSubmit={(data) => alert(JSON.stringify(data, null, 2))} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default {
  title: "complex/FormGen",
  component: FormGenSandbox,
  parameters: {
    layout: "fullscreen",
  },
  tags: ["autodocs"],
  argTypes: {},
} satisfies Meta<typeof FormGenSandbox>;

type Story = StoryObj<typeof FormGenSandbox>;

export const Default: Story = {};
